import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import {
  Button,
  DatePicker,
  message,
  Modal,
  Progress,
  Space,
  Steps,
  Typography,
} from "antd";
import dayjs from "dayjs";
import ExcelJS from "exceljs/dist/exceljs.min.js";

import {
  BACKEND_DATE_FORMAT,
  COLORS,
  UI_DATE_FORMAT,
} from "../../../../consts";

import {
  calculateRowDimensions,
  calculateRowLength,
  cleanGhName,
  fetchOpenMeteoTemperatureData,
  filterVisiblePlotData,
  isDisplayedLeafSizeWithinRange,
} from "./utils";
import { getGreenhousePlotData } from "../../../../utils/api.js";
import {
  buildGreenhouseForecastRows,
  MAX_ROW_TASKS_CONCURRENCY,
  prepareGreenhouseForecastContext,
  prepareForecastInputsForGreenhouse,
} from "./spinachForecastSummaryBuilder";
import { useUser } from "../../../../context/UserContext";
import {
  applyCorrectedForecastForDisplay,
  isPositiveTrendCorrectionEnabled,
} from "../../../../utils/positiveTrendCorrection";

const { Text } = Typography;
const { RangePicker } = DatePicker;

/* ---------- helpers ---------- */
const collator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});
const natCmp = (a = "", b = "") => collator.compare(String(a), String(b));
const numOrNull = (v) => {
  if (v == null || v === "" || v === "-") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const hexToARGB = (hex) => {
  if (!hex) return "FF666666";
  const h = hex.replace("#", "");
  const full =
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h;
  return ("FF" + full).toUpperCase();
};

const tagFill = (hex) => ({
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: hexToARGB(hex) },
});

const whiteText = { color: { argb: "FFFFFFFF" } };

const sanitizeFilename = (s) =>
  String(s || "")
    .replace(/[/\\?%*:|"<>]/g, "")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .trim();

const STEP_TITLES = [
  { upcoming: "Prepare Data", active: "Preparing Data", done: "Data Prepared" },
  { upcoming: "Build Excel", active: "Building Excel", done: "Excel Built" },
  { upcoming: "Download", active: "Downloading", done: "Downloaded" },
  { upcoming: "Finish", active: "Finishing", done: "Finished" },
];

const normalizeErrorReason = (error) => {
  if (!error) return "unknown error";
  if (typeof error === "string") return error;
  return error?.message || String(error);
};

const createOpenMeteoExportError = (reason) =>
  new Error(`Open-Meteo API failed: ${reason}. Please try again.`);

const createTemperatureExportError = (startDateStr, endDateStr) =>
  createOpenMeteoExportError(
    `missing complete temperature data for ${startDateStr} to ${endDateStr}. Export cancelled to avoid incomplete forecast values`,
  );

const wrapOpenMeteoExportError = (error) =>
  new Error(
    `Open-Meteo API failed: ${normalizeErrorReason(error)}. Please try again.`,
  );

const hasCompleteTemperatureCoverage = (
  temperatureData,
  startDateStr,
  endDateStr,
) => {
  const startDate = dayjs(startDateStr, BACKEND_DATE_FORMAT);
  const endDate = dayjs(endDateStr, BACKEND_DATE_FORMAT);
  if (!startDate.isValid() || !endDate.isValid() || endDate.isBefore(startDate)) {
    return false;
  }

  const coveredDates = new Set(
    temperatureData
      .filter(
        (item) =>
          item?.date &&
          Number.isFinite(Number(item.max)) &&
          Number.isFinite(Number(item.min)),
      )
      .map((item) => dayjs(item.date).format(BACKEND_DATE_FORMAT)),
  );

  for (
    let cursor = startDate;
    !cursor.isAfter(endDate, "day");
    cursor = cursor.add(1, "day")
  ) {
    if (!coveredDates.has(cursor.format(BACKEND_DATE_FORMAT))) {
      return false;
    }
  }

  return true;
};

const buildExportTemperatureSliceFetcher = async ({
  forecastInputMaps,
  latLong,
  siteMetadata,
}) => {
  let minDate = null;
  let maxDate = null;

  forecastInputMaps.forEach((forecastInputs) => {
    forecastInputs.forEach(({ sliceStart, sliceEnd }) => {
      const startDate = dayjs(sliceStart, BACKEND_DATE_FORMAT);
      const endDate = dayjs(sliceEnd, BACKEND_DATE_FORMAT).add(5, "day");
      if (!startDate.isValid() || !endDate.isValid()) return;
      if (!minDate || startDate.isBefore(minDate)) minDate = startDate;
      if (!maxDate || endDate.isAfter(maxDate)) maxDate = endDate;
    });
  });

  const rangeCache = new Map();
  let prefetched = [];
  if (minDate?.isValid() && maxDate?.isValid() && !maxDate.isBefore(minDate)) {
    const minDateStr = minDate.format(BACKEND_DATE_FORMAT);
    const maxDateStr = maxDate.format(BACKEND_DATE_FORMAT);
    let fetched;
    try {
      fetched = await fetchOpenMeteoTemperatureData(
        latLong,
        minDateStr,
        maxDateStr,
        siteMetadata,
      );
    } catch (error) {
      throw wrapOpenMeteoExportError(error);
    }
    prefetched = Array.isArray(fetched)
      ? [...fetched].sort((a, b) => String(a.date).localeCompare(String(b.date)))
      : [];
    if (
      !prefetched.length ||
      !hasCompleteTemperatureCoverage(prefetched, minDateStr, maxDateStr)
    ) {
      throw createTemperatureExportError(minDateStr, maxDateStr);
    }
  }

  return async (startDateStr, endDateStr) => {
    const key = `${startDateStr}|${endDateStr}`;
    if (rangeCache.has(key)) return rangeCache.get(key);

    if (prefetched.length) {
      const firstDate = prefetched[0].date;
      const lastDate = prefetched[prefetched.length - 1].date;
      if (startDateStr >= firstDate && endDateStr <= lastDate) {
        const sliced = prefetched.filter(
          (item) => item.date >= startDateStr && item.date <= endDateStr,
        );
        if (
          sliced.length &&
          hasCompleteTemperatureCoverage(sliced, startDateStr, endDateStr)
        ) {
          rangeCache.set(key, sliced);
          return sliced;
        }
      }
    }

    throw createTemperatureExportError(startDateStr, endDateStr);
  };
};

/* ----------------- component ----------------- */
const ExportHarvestedExcelModal = ({
  visible,
  onCancel,
  currentOrg,
  currentSite,
  currentCrop,
  greenhouseData,
  siteMetadata,
  excelRawData,
  latLong,
  gddBaseTemperature,

  fetchHarvestedData,
  createHarvestedMap,
  extractTableDataFromRawData,
  getAverageRowLevelData,
  getSortedFilteredDatesRange,
  memoizedCalcDiffRange,
  getForecastLeafSize,

  minimumIdealHarvestLeafSize,
  maximumIdealHarvestLeafSize,

  // labels
  greenhouseLabel = "Greenhouse",
  blockLabel = "Block",
  yieldTableHeaderName = "Yield",
  showWeightColumn = false,
  weightTableHeaderName = "Weight",
}) => {
  const { isAdmin } = useUser();
  // OF-129: match the customer-facing (corrected) dashboard so a customer's
  // export equals their screen; admins/non-opted-in sites export raw.
  const trendCorrectionEnabled = useMemo(
    () =>
      isPositiveTrendCorrectionEnabled({
        isAdmin,
        site: currentSite,
        cropName: currentCrop?.name,
      }),
    [isAdmin, currentSite, currentCrop],
  );

  const [step, setStep] = useState(0);
  const [rowProgress, setRowProgress] = useState({ done: 0, total: 0 });
  const [busy, setBusy] = useState(false);
  const [exportStarted, setExportStarted] = useState(false);
  const [harvestDateRange, setHarvestDateRange] = useState(null);
  const cancelRef = useRef(false);

  const yieldPerTubs =
    siteMetadata?.FORECAST_GREENHOUSE_TABLE?.YIELD_PER_TUBS || 1;

  /**
   * Harvested weight of a row, in kg. Prefers the weight the grower recorded
   * over `count * YIELD_PER_TUBS`, so the exported yield columns match the
   * dashboard and the weights the site actually measured.
   */
  const resolveTotalYield = (rawYield, weight) => {
    const w = Number(weight);
    if (Number.isFinite(w) && w > 0) return w;
    return (Number(rawYield) || 0) * yieldPerTubs;
  };

  // tiny caches for geometry (key: `${gh}|${plot}`)
  const lenCacheRef = useRef(new Map());
  const dimCacheRef = useRef(new Map());

  const checkCanceled = () => cancelRef.current;

  const isHarvestDateInExportScope = useCallback(
    (harvestDate) => {
      if (!harvestDate) return false;
      const hasHarvestDateRange =
        harvestDateRange?.length === 2 &&
        harvestDateRange[0] &&
        harvestDateRange[1];
      if (!hasHarvestDateRange) return true;

      const parsedHarvestDate = dayjs(harvestDate, BACKEND_DATE_FORMAT);
      if (!parsedHarvestDate.isValid()) return false;

      const startDate = harvestDateRange[0].startOf("day");
      const endDate = harvestDateRange[1].endOf("day");
      return (
        !parsedHarvestDate.isBefore(startDate, "day") &&
        !parsedHarvestDate.isAfter(endDate, "day")
      );
    },
    [harvestDateRange],
  );

  // below: const computeYieldPerHa = (...) => { ... }
  const getRowLengthMeters = (ghName, plotName) => {
    const key = `${ghName}|${plotName}`;
    let len = lenCacheRef.current.get(key);
    if (len == null) {
      const name = cleanGhName(ghName);
      const coords = siteMetadata?.GREENHOUSE_COORDINATES?.[name]?.[plotName];
      if (!Array.isArray(coords) || coords.length < 3) return null;
      len = calculateRowLength(coords);
      lenCacheRef.current.set(key, len || 0);
    }
    if (!len || len <= 0) return null;
    return Number(len.toFixed(1));
  };

  /* ---------- headers & widths (memoized on weight toggle) ---------- */
  const { headers, widths, withinIdx, ypmIdx } = useMemo(() => {
    const baseHeaders = [
      greenhouseLabel,
      blockLabel,
      "Last Length (cm)",
      "Last Data Collection Date",
      "Day 1 (cm)",
      "Day 2 (cm)",
      "Day 3 (cm)",
      "Day 4 (cm)",
      "Day 5 (cm)",
      "Harvest Date",
      yieldTableHeaderName,
      ...(showWeightColumn ? [weightTableHeaderName] : []),
      "Within Recommendation",
      "Row Lengths",
      "Yield per Meter (Kg/m)",
      "Yield per Hectare (ton/Ha)",
    ];
    const baseWidths = [
      18,
      12,
      16,
      20,
      14,
      14,
      14,
      14,
      14,
      16,
      12,
      ...(showWeightColumn ? [12] : []),
      24,
      20,
      22,
      28,
    ];
    return {
      headers: baseHeaders,
      widths: baseWidths,
      withinIdx: baseHeaders.indexOf("Within Recommendation") + 1,
      ypmIdx: baseHeaders.indexOf("Yield per Meter (Kg/m)") + 1,
    };
  }, [
    greenhouseLabel,
    blockLabel,
    yieldTableHeaderName,
    weightTableHeaderName,
    showWeightColumn,
  ]);

  /* ----------------- summary utils ----------------- */
  const buildSummaryIndex = (summary) => {
    const idx = {};
    for (const [ghName, rows] of Object.entries(summary || {})) {
      const map = new Map();
      rows?.forEach((r) => map.set(r.plotId, r));
      idx[ghName] = map;
    }
    return idx;
  };

  const getExportLengths = (row, summaryIndex) => {
    const rec = summaryIndex?.[row.ghName]?.get(row.plotId);
    if (!rec) {
      return { last: "", lastDate: "", d1: "", d2: "", d3: "", d4: "", d5: "" };
    }

    const last = numOrNull(rec?.lastData?.averagePercentile80_95);
    const lastDate = rec?.lastData?.date ? rec?.lastData?.date : "N/A";

    const totalFor = (key) => {
      const d = rec[key];
      if (typeof d === "string") return "N/A";
      if (!d || typeof d !== "object" || !d.date) return "N/A";
      const inc = numOrNull(d.averagePercentile80_95);
      if (last == null || inc == null) return "N/A";
      return (last + inc).toFixed(1);
    };

    return {
      last: last == null ? "N/A" : last.toFixed(1),
      lastDate,
      d1: totalFor("day1"),
      d2: totalFor("day2"),
      d3: totalFor("day3"),
      d4: totalFor("day4"),
      d5: totalFor("day5"),
    };
  };

  const withinRecFor = (row, summaryIndex) => {
    const rec = summaryIndex?.[row.ghName]?.get(row.plotId);
    if (!rec || !row.harvestDate)
      return { text: "N/A", color: COLORS.alertYellow };

    const inRange = (v) =>
      isDisplayedLeafSizeWithinRange(
        v,
        minimumIdealHarvestLeafSize,
        maximumIdealHarvestLeafSize,
      );

    const harvestDateBackend = row.harvestDate;

    if (rec?.lastData?.date) {
      const lastBackend = dayjs(rec.lastData.date, UI_DATE_FORMAT).format(
        BACKEND_DATE_FORMAT,
      );
      if (lastBackend === harvestDateBackend) {
        return inRange(rec.lastData.averagePercentile80_95)
          ? { text: "Yes", color: COLORS.successGreen }
          : { text: "No", color: COLORS.dangerRed };
      }
    }
    const current = numOrNull(rec?.lastData?.averagePercentile80_95);
    for (const key of ["day1", "day2", "day3", "day4", "day5"]) {
      const d = rec[key];
      if (d && typeof d === "object" && d.date) {
        const fBackend = dayjs(d.date, UI_DATE_FORMAT).format(
          BACKEND_DATE_FORMAT,
        );
        if (fBackend === harvestDateBackend) {
          const inc = numOrNull(d?.averagePercentile80_95);
          if (current == null || inc == null) {
            return { text: "No", color: COLORS.dangerRed };
          }
          const total = current + inc;
          return inRange(total)
            ? { text: "Yes", color: COLORS.successGreen }
            : { text: "No", color: COLORS.dangerRed };
        }
      }
    }
    return { text: "No", color: COLORS.dangerRed };
  };

  const computeYieldPerMeter = (ghName, plotName, rawYield, weight) => {
    if (!rawYield || rawYield <= 0)
      return { val: null, color: COLORS.darkGray };
    const key = `${ghName}|${plotName}`;
    let len = lenCacheRef.current.get(key);
    if (len == null) {
      const coords =
        siteMetadata?.GREENHOUSE_COORDINATES?.[
          ghName
            .replace(" (S1)", "")
            .replace(" (S3)", "")
            .replace(" (S2)", "")
            .replace(" (OLD)", "")
        ]?.[plotName];
      if (!Array.isArray(coords) || coords.length < 3)
        return { val: null, color: COLORS.darkGray };
      len = calculateRowLength(coords);
      lenCacheRef.current.set(key, len || 0);
    }
    if (!len || len <= 0) return { val: null, color: COLORS.darkGray };

    const totalYieldKg = resolveTotalYield(rawYield, weight);
    if (totalYieldKg <= 0) return { val: null, color: COLORS.darkGray };
    const ypm = totalYieldKg / len;

    const minYpm =
      siteMetadata?.FORECAST_GREENHOUSE_TABLE
        ?.MINIMUM_IDEAL_VALUE_YIELD_PER_METER || 1;
    const maxYpm =
      siteMetadata?.FORECAST_GREENHOUSE_TABLE
        ?.MAXIMUM_IDEAL_VALUE_YIELD_PER_METER || 3;

    const color =
      ypm < minYpm
        ? COLORS.dangerRed
        : ypm > maxYpm
          ? COLORS.successGreen
          : COLORS.primaryBlue;

    return { val: Number(ypm.toFixed(1)), color };
  };

  const computeYieldPerHa = (ghName, plotName, rawYield, weight) => {
    if (!rawYield || rawYield <= 0) return null;
    const key = `${ghName}|${plotName}`;
    let dims = dimCacheRef.current.get(key);
    if (!dims) {
      const coords =
        siteMetadata?.GREENHOUSE_COORDINATES?.[
          ghName
            .replace(" (S1)", "")
            .replace(" (S3)", "")
            .replace(" (S2)", "")
            .replace(" (OLD)", "")
        ]?.[plotName];
      if (!Array.isArray(coords) || coords.length < 3) return null;
      dims = calculateRowDimensions(coords);
      dimCacheRef.current.set(key, dims);
    }
    if (!dims?.length || !dims?.width) return null;

    const totalYieldKg = resolveTotalYield(rawYield, weight);
    if (totalYieldKg <= 0) return null;
    const area = dims.length * dims.width;
    const kgPerM2 = totalYieldKg / area;
    return Number((kgPerM2 * 10).toFixed(2)); // ton/ha
  };

  const buildRowsUnion = (fullSummary) => {
    const map = new Map();
    Object.entries(fullSummary || {}).forEach(([ghName, arr]) => {
      (arr || []).forEach((rec) => {
        if (!rec?.isHarvested || !rec?.harvestDate) return;
        const key = `${rec.greenhouseId}-${rec.plotId}`;
        if (map.has(key)) return;
        map.set(key, {
          ghId: rec.greenhouseId,
          ghName,
          plotId: rec.plotId,
          plotName: rec.plotName,
          harvestDate: rec.harvestDate,
          yield: rec.yield ?? 0,
          weight: rec.weight ?? 0,
        });
      });
    });
    const out = Array.from(map.values());
    out.sort((a, b) => {
      const ta = dayjs(a.harvestDate, BACKEND_DATE_FORMAT).valueOf() || 0;
      const tb = dayjs(b.harvestDate, BACKEND_DATE_FORMAT).valueOf() || 0;
      if (tb !== ta) return tb - ta; // latest first
      const ghCmp = natCmp(a.ghName, b.ghName);
      if (ghCmp) return ghCmp;
      return natCmp(a.plotName, b.plotName);
    });
    return out;
  };

  /* ----------------- build export summary (parallelized) ----------------- */
  const buildSummaryForecast = useCallback(
    async (excelRows) => {
      if (!excelRows) return {};

      const greenhouseIds = greenhouseData.map((g) => g.id).filter(Boolean);
      const graphRes = await getGreenhousePlotData(
        currentOrg,
        currentSite.id,
        currentCrop.id,
        greenhouseIds,
      );
      if (checkCanceled()) return {};
      const allRowGraph = filterVisiblePlotData(graphRes?.data?.data || {});

      const harvestedData = await fetchHarvestedData(true);
      const harvestedMap = createHarvestedMap(harvestedData);

      const exportSummaryData = {};
      const filterRules =
        currentSite?.metadata?.[currentCrop?.name]
          ?.FORECAST_GENERIC_FILTER_RULES || [];

      const greenhouseContexts = greenhouseData.map((greenhouse) => ({
        greenhouse,
        ...prepareGreenhouseForecastContext({
          greenhouse,
          excelRows,
          allRowGraph,
          harvestedMap,
          extractTableDataFromRawData,
          includeHarvestState: true,
        }),
      }));
      if (checkCanceled()) return {};

      const forecastInputMaps = new Map();
      await Promise.all(
        greenhouseContexts.map(async ({ greenhouse, rowDataMap }) => {
          const ghName = greenhouse.name;
          const forecastInputs = await prepareForecastInputsForGreenhouse({
            greenhouse,
            rowDataMap,
            allRowGraph,
            filterRules,
            latLong,
            getAverageRowLevelData,
            getSortedFilteredDatesRange,
            maxRowTasksConcurrency: MAX_ROW_TASKS_CONCURRENCY,
            shouldBuildForecastForRow: ({ tableData, isHarvestedRecord }) =>
              isHarvestedRecord &&
              isHarvestDateInExportScope(tableData?.harvestDate || ""),
          });
          forecastInputMaps.set(ghName, forecastInputs);
        }),
      );
      if (checkCanceled()) return {};

      const exportTempSliceFetcher = await buildExportTemperatureSliceFetcher({
        forecastInputMaps,
        latLong,
        siteMetadata,
      });
      if (checkCanceled()) return {};

      await Promise.all(
        greenhouseContexts.map(async ({ greenhouse, rowDataMap }) => {
          if (checkCanceled()) return;
          const ghName = greenhouse.name;

          try {
            const rowForecasts = await buildGreenhouseForecastRows({
              greenhouse,
              rowDataMap,
              allRowGraph,
              filterRules,
              latLong,
              gddBaseTemperature,
              getAverageRowLevelData,
              getSortedFilteredDatesRange,
              memoizedCalcDiffRange,
              getTempSliceOrFetch: exportTempSliceFetcher,
              getForecastLeafSize,
              maxRowTasksConcurrency: MAX_ROW_TASKS_CONCURRENCY,
              forecastRowConcurrency: MAX_ROW_TASKS_CONCURRENCY,
              passTempFetcherToForecast: true,
              failOnForecastRowError: true,
              precomputedForecastInputs: forecastInputMaps.get(ghName),
              includeHarvestState: true,
              correctNegativeTrend: trendCorrectionEnabled,
              shouldBuildForecastForRow: ({ tableData, isHarvestedRecord }) =>
                isHarvestedRecord &&
                isHarvestDateInExportScope(tableData?.harvestDate || ""),
            });

            exportSummaryData[ghName] = rowForecasts;
          } catch (error) {
            console.error(`Export build error in ${ghName}:`, error);
            throw error;
          }
        }),
      );

      // OF-129: swap in the corrected forecast for customer exports (no-op when
      // disabled). Never used for anything persisted to the backend.
      return trendCorrectionEnabled
        ? applyCorrectedForecastForDisplay(exportSummaryData)
        : exportSummaryData;
    },
    [
      greenhouseData,
      currentOrg,
      currentSite?.id,
      currentSite?.metadata,
      currentCrop?.id,
      currentCrop?.name,
      siteMetadata,
      latLong,
      gddBaseTemperature,
      fetchHarvestedData,
      createHarvestedMap,
      isHarvestDateInExportScope,
      extractTableDataFromRawData,
      getAverageRowLevelData,
      getSortedFilteredDatesRange,
      memoizedCalcDiffRange,
      getForecastLeafSize,
      trendCorrectionEnabled,
    ],
  );

  /* ----------------- export pipeline ----------------- */
  const startExport = async () => {
    if (!visible || busy) return;
    cancelRef.current = false;
    setExportStarted(true);
    setBusy(true);
    setStep(0);

    try {
      // 0) Build a fresh export summary so the workbook uses current row data.
      const fullSummary = await buildSummaryForecast(excelRawData);
      if (checkCanceled()) return;

      const rows = buildRowsUnion(fullSummary);
      const filteredRows = rows.filter((row) =>
        isHarvestDateInExportScope(row.harvestDate),
      );
      const summaryIndex = buildSummaryIndex(fullSummary);

      // Precompute static maps so the Excel loop is dumb
      const withinMap = new Map();
      const lengthsMap = new Map();
      filteredRows.forEach((r) => {
        const k = `${r.ghId}-${r.plotId}`;
        withinMap.set(k, withinRecFor(r, summaryIndex));
        lengthsMap.set(k, getExportLengths(r, summaryIndex));
      });

      setStep(1);
      setRowProgress({ done: 0, total: filteredRows.length });

      // 1) Build workbook
      const wb = new ExcelJS.Workbook();
      const sheet = wb.addWorksheet("Harvested");
      sheet.addRow(headers);
      sheet.getRow(1).font = { bold: true };
      widths.forEach((w, i) => (sheet.getColumn(i + 1).width = w));

      // 2) Fill rows (throttled progress + occasional yield)
      const PROGRESS_EVERY = 50;
      for (let i = 0; i < filteredRows.length; i++) {
        if (checkCanceled()) return;
        if (i % 200 === 0) await new Promise((r) => setTimeout(r, 0));

        const r = filteredRows[i];
        const key = `${r.ghId}-${r.plotId}`;

        const within = withinMap.get(key);
        const ypm = computeYieldPerMeter(r.ghName, r.plotName, r.yield, r.weight);
        const yph = computeYieldPerHa(r.ghName, r.plotName, r.yield, r.weight);
        const lengths = lengthsMap.get(key);
        const rowLen = getRowLengthMeters(r.ghName, r.plotName);
        const cells = [
          r.ghName,
          r.plotName,
          lengths.last,
          lengths.lastDate,
          lengths.d1,
          lengths.d2,
          lengths.d3,
          lengths.d4,
          lengths.d5,
          r.harvestDate
            ? dayjs(r.harvestDate, BACKEND_DATE_FORMAT).format(UI_DATE_FORMAT)
            : "",
          r.yield ?? "",
          ...(showWeightColumn ? [r.weight ?? ""] : []),
          within.text,
          rowLen ?? "",
          ypm.val ?? "",
          yph ?? "",
        ];

        const excelRow = sheet.addRow(cells);

        const withinCell = excelRow.getCell(withinIdx);
        withinCell.fill = tagFill(within.color);
        withinCell.font = whiteText;
        withinCell.alignment = { vertical: "middle", horizontal: "center" };

        if (ypm.val !== null) {
          const ypmCell = excelRow.getCell(ypmIdx);
          ypmCell.fill = tagFill(ypm.color);
          ypmCell.font = whiteText;
          ypmCell.alignment = { vertical: "middle", horizontal: "center" };
        }

        // throttled progress updates
        if (i % PROGRESS_EVERY === 0 || i === filteredRows.length - 1) {
          setRowProgress({ done: i + 1, total: filteredRows.length });
        }
      }

      setStep(2);

      // 3) Download
      const sitePart = sanitizeFilename(
        currentSite?.displayName || currentSite?.name || "site",
      );
      const cropPart = sanitizeFilename(
        currentCrop?.displayName || currentCrop?.name || "crop",
      );
      const fileName = `Harvested_${sitePart}_${cropPart}_${dayjs().format("YYYY-MM-DD_HHmm")}.xlsx`;

      const buffer = await wb.xlsx.writeBuffer();
      if (checkCanceled()) return;

      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      setStep(3);
      if (!checkCanceled()) message.success("Export complete.");
      setTimeout(() => !checkCanceled() && onCancel(), 300);
    } catch (e) {
      console.error(e);
      message.error(
        e?.response?.data?.message || e?.message || "Export failed.",
      );
      setExportStarted(false);
    } finally {
      setBusy(false);
    }
  };

  /* ----------------- lifecycle ----------------- */
  useEffect(() => {
    if (visible) {
      cancelRef.current = false;
      setStep(0);
      setRowProgress({ done: 0, total: 0 });
      setBusy(false);
      setExportStarted(false);
      setHarvestDateRange(null);
      // reset caches per run
      lenCacheRef.current.clear();
      dimCacheRef.current.clear();
    } else {
      cancelRef.current = true;
    }
  }, [visible]);

  const steps = STEP_TITLES.map((t, i) => ({
    title: step > i ? t.done : step === i ? t.active : t.upcoming,
  }));

  return (
    <Modal
      open={visible}
      onCancel={() => {
        cancelRef.current = true;
        onCancel();
      }}
      title={`Export Harvested ${greenhouseLabel}s`}
      footer={
        exportStarted ? (
          <Space>
            <Button
              danger
              onClick={() => {
                cancelRef.current = true;
                onCancel();
              }}
            >
              Cancel
            </Button>
          </Space>
        ) : (
          <Space>
            <Button onClick={onCancel}>Cancel</Button>
            <Button type="primary" onClick={startExport}>
              Export
            </Button>
          </Space>
        )
      }
      width={720}
      centered
      maskClosable={false}
      destroyOnHidden
    >
      {!exportStarted ? (
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Text>
            Select an optional harvest date range. Leave it empty to export all
            harvested rows.
          </Text>
          <RangePicker
            value={harvestDateRange}
            onChange={setHarvestDateRange}
            format="YYYY-MM-DD"
            allowClear
            style={{ width: "100%" }}
          />
        </Space>
      ) : (
        <>
          <Steps current={step} items={steps} style={{ marginBottom: 12 }} />
          {step === 0 && <Text type="secondary">Preparing full dataset…</Text>}
          {step === 1 && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 8,
                }}
              >
                <Text>
                  Rows to export: <b>{rowProgress.total}</b>
                </Text>
                <Text>
                  Progress:{" "}
                  <b>
                    {rowProgress.done}/{rowProgress.total}
                  </b>
                </Text>
              </div>
              <Progress
                percent={
                  rowProgress.total
                    ? Math.round((rowProgress.done / rowProgress.total) * 100)
                    : 0
                }
                status="active"
              />
              <Text type="secondary">
                Applying colors and formatting to match your table.
              </Text>
            </>
          )}
          {step === 2 && <Text>Generating file and triggering download…</Text>}
          {step === 3 && <Text strong>Done.</Text>}
        </>
      )}
    </Modal>
  );
};

export default ExportHarvestedExcelModal;
